// Régua de prazo e leitura da situação de uma licitação — compartilhada pela
// coluna da lista (AbaOportunidades) e pelo modal (ModalLicitacao).
//
// Por que existe (2026-09-02): a classificação "vencida há Nd / HOJE / em N
// dias" e o mapa de situação do ConLicitação viviam inline na coluna da
// tabela. O modal precisava mostrar o MESMO prazo com a MESMA cor, e copiar
// o bloco criaria duas réguas que divergem na primeira manutenção.
//
// 🚨 A régua OFICIAL do prazo é a do backend (`prazoEfetivo()` em
// handlers/licitacoes.js): dataEncerramentoProposta (PNCP) → datahora_prazo
// (ConLicitação) → data_abertura. `prazoEfetivoCliente` só REPETE essa ordem
// para linhas antigas/caches que ainda não trazem `prazo_efetivo` — não é
// lugar de inventar regra nova.
//
// 🚨 Fuso (28/09/2026): `prazo_efetivo` e `data_abertura` são HORA DE PAREDE
// de Brasília e chegam como texto 'AAAA-MM-DDTHH:MM:SS', sem Z nem offset.
// `new Date(s)` lê esse formato como hora local — a certa, para quem usa o
// sistema em Brasília. Antes o servidor rotulava a parede como UTC ('…Z') e a
// lista e a ficha mostravam o prazo 3 h ANTES do edital. Os carimbos
// (`importado_em`, `triada_em`, `criado_em`…) são UTC de verdade e seguem com
// Z. Na volta (a data da ficha), a hora sai no mesmo formato de parede —
// `paredeDoDatetimeLocal`. Mesmo contrato do Meu Radar (meu-radar/api.ts).

export interface PrazoClassificado {
  /** Dias até o prazo (negativo = já passou). Arredondado para cima como na lista. */
  dias: number;
  /** Cor hex do texto (vermelho/âmbar/verde/cinza) — mesma escala da coluna. */
  cor: string;
  /** Texto curto: "vencida há 3d", "HOJE", "amanhã", "em 12 dias". */
  rotulo: string;
  /** Prefixo de ícone ("⚠ ", "🔥 " ou vazio). */
  icone: string;
  /** Negrito quando falta ≤ 3 dias ou já venceu. */
  bold: boolean;
  /** A data interpretada, para formatar ao lado do rótulo. */
  data: Date;
}

/**
 * Classifica um prazo ISO em dias/cor/rótulo. Extraído SEM mudança de
 * comportamento da coluna "Abertura" (L16a, 2026-06-03).
 * Devolve null só para string que não vira data — a coluna mostra "—".
 *
 * 🚨 (28/09/2026) LEGADO, mantido de propósito até o Meu Radar sair dele: a
 * lista e a ficha de Oportunidades usam `classificarPrazoEdital`, abaixo. O
 * Meu Radar da main ainda chama esta função com o prazo rotulado de UTC
 * ('…Z', 3 h antes); trocar a conta aqui faria o radar dizer "vencida hoje"
 * nas 3 h anteriores ao prazo. O Meu Radar novo (PR #294) troca esta função
 * por `classificarPrazoRadar` — depois dele, esta fica sem uso e pode sair.
 */
export function classificarPrazo(iso: string | null | undefined): PrazoClassificado | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const dias = Math.ceil((d.getTime() - Date.now()) / 86400000);
  let rotulo: string;
  let cor: string;
  let icone = '';
  let bold = false;
  if (dias < 0)        { rotulo = `vencida há ${Math.abs(dias)}d`; cor = '#B91626'; icone = '⚠ '; bold = true; }
  else if (dias === 0) { rotulo = 'HOJE';              cor = '#B91626'; icone = '🔥 '; bold = true; }
  else if (dias === 1) { rotulo = 'amanhã';            cor = '#B91626'; bold = true; }
  else if (dias <= 3)  { rotulo = `em ${dias} dias`;   cor = '#B91626'; bold = true; }
  else if (dias <= 7)  { rotulo = `em ${dias} dias`;   cor = '#B07500'; }
  else if (dias <= 30) { rotulo = `em ${dias} dias`;   cor = '#0a7f4a'; }
  else                 { rotulo = `em ${dias} dias`;   cor = '#444'; }
  return { dias, cor, rotulo, icone, bold, data: d };
}

export interface PrazoEditalClassificado extends PrazoClassificado {
  /** Aqui `dias` é de CALENDÁRIO (0 = vence hoje; negativo = já passou). */
  dias: number;
  /** O prazo já passou (inclusive mais cedo, hoje). Nunca vem com 🔥. */
  vencida: boolean;
}

/**
 * Classifica o prazo do EDITAL em dias/cor/rótulo — a coluna "Prazo" da lista
 * e o prazo da ficha de Oportunidades. Devolve null só para string que não
 * vira data (a coluna mostra "—"). `agora` é o relógio de referência (o teste
 * passa um falso). Rótulos: "vencida há 3d", "vencida hoje", "HOJE",
 * "amanhã", "em 12 dias".
 *
 * 🚨 (28/09/2026) Conta DIAS DE CALENDÁRIO, não blocos de 24 h. A conta de
 * `classificarPrazo` (`Math.ceil` dos milissegundos) dizia "amanhã" para um
 * edital que fecha HOJE mais tarde e "🔥 HOJE" para um que já tinha fechado —
 * o que o prazo 3 h adiantado disfarçava: consertado o fuso, o edital das
 * 10:00 visto às 08:00 passaria de "HOJE" para "amanhã". A regra é a mesma do
 * Meu Radar (`classificarPrazoRadar`, PR #294); as faixas de cor são as de
 * sempre. Escrita sem anotação de tipo no corpo: o teste offline executa esta
 * função.
 */
export function classificarPrazoEdital(iso: string | null | undefined, agora?: number): PrazoEditalClassificado | null {
  if (!iso) return null;
  // O ConLicitação cru usa espaço no lugar do 'T' (fallback de linha antiga);
  // o Safari não lê essa forma.
  const txt = String(iso);
  const d = new Date(/^\d{4}-\d{2}-\d{2} \d/.test(txt) ? txt.replace(' ', 'T') : txt);
  if (Number.isNaN(d.getTime())) return null;
  const ref = new Date(agora === undefined ? Date.now() : agora);
  // Meia-noite a meia-noite em Date.UTC: não sofre horário de verão.
  const dias = Math.round(
    (Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())
      - Date.UTC(ref.getFullYear(), ref.getMonth(), ref.getDate())) / 86400000);
  if (d.getTime() < ref.getTime()) {
    const passados = Math.min(dias, 0);
    const rotulo = passados < 0 ? `vencida há ${Math.abs(passados)}d` : 'vencida hoje';
    return { dias: passados, vencida: true, cor: '#B91626', rotulo, icone: '⚠ ', bold: true, data: d };
  }
  if (dias <= 0) return { dias: 0, vencida: false, cor: '#B91626', rotulo: 'HOJE', icone: '🔥 ', bold: true, data: d };
  if (dias === 1) return { dias, vencida: false, cor: '#B91626', rotulo: 'amanhã', icone: '', bold: true, data: d };
  if (dias <= 3) return { dias, vencida: false, cor: '#B91626', rotulo: `em ${dias} dias`, icone: '', bold: true, data: d };
  if (dias <= 7) return { dias, vencida: false, cor: '#B07500', rotulo: `em ${dias} dias`, icone: '', bold: false, data: d };
  if (dias <= 30) return { dias, vencida: false, cor: '#0a7f4a', rotulo: `em ${dias} dias`, icone: '', bold: false, data: d };
  return { dias, vencida: false, cor: '#444', rotulo: `em ${dias} dias`, icone: '', bold: false, data: d };
}

/**
 * Valor do <input type="datetime-local"> ("AAAA-MM-DDTHH:mm", hora local) →
 * parede de Brasília "AAAA-MM-DDTHH:mm:ss", SEM offset — o formato que o
 * servidor guarda. Não passa por `toISOString()`: o Postgres ignora o 'Z' ao
 * gravar num TIMESTAMP sem fuso, e 10:00 digitado virava 13:00 no banco.
 * Vazio continua vazio (o servidor grava null).
 */
export function paredeDoDatetimeLocal(v: string | null | undefined): string {
  const s = String(v || '').trim();
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(s)) return `${s}:00`;
  return s;
}

/** "dd/mm/aa hh:mm" no fuso do navegador — formato que a lista já usava. */
export function fmtDataHoraCurta(iso: string | Date | null | undefined): string {
  if (!iso) return '—';
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

export interface SituacaoLegivel {
  /** Nome humano: "Prorrogada", "Suspensa", ou o texto do PNCP como veio. */
  txt: string;
  /** Cor de Tag do antd. */
  cor: string;
  /** Explicação para o Tooltip. */
  tip: string;
  /**
   * Texto alternativo que a coluna da lista mostra quando a licitação NÃO
   * tem data (L15.4). Só os quatro códigos originais têm — os demais caem
   * em `txt`. Mantido para a lista continuar igual ao que era.
   */
  semData?: string;
  /**
   * `true` só para código do ConLicitação que sabemos traduzir. A coluna da
   * lista usa isto para decidir se mostra o rótulo no lugar da data: código
   * desconhecido (ou texto livre de licitação manual) continua virando "—"
   * ali, porque naquele espaço de 9 px um código cru não diz nada.
   */
  conhecida?: boolean;
}

// Códigos de situação dos boletins do ConLicitação. Os que "podem voltar"
// (PRO/REAB/ADIA/SUS/RET/ALTER) espelham SITUACOES_PODEM_VOLTAR do backend;
// ANULA/REVO/CANCE são fim de linha.
const SITUACOES_CONLIC: Record<string, SituacaoLegivel> = {
  NOVA:  { txt: 'Nova',        cor: 'blue',    tip: 'Nova publicação — data virá em boletim seguinte', semData: 'aguardando data' },
  URGEN: { txt: 'Urgente',     cor: 'orange',  tip: 'Aviso de urgência — data pendente',               semData: 'urgência' },
  PRO:   { txt: 'Prorrogada',  cor: 'orange',  tip: 'Prorrogação anunciada — nova data virá',          semData: 'prorrogação' },
  SUS:   { txt: 'Suspensa',    cor: 'gold',    tip: 'Licitação suspensa pelo órgão',                    semData: 'suspensa' },
  SUSS:  { txt: 'Suspensa',    cor: 'gold',    tip: 'Licitação suspensa pelo órgão' },
  REAB:  { txt: 'Reaberta',    cor: 'green',   tip: 'Licitação reaberta pelo órgão' },
  ADIA:  { txt: 'Adiada',      cor: 'orange',  tip: 'Abertura adiada — nova data virá' },
  ADIS:  { txt: 'Adiada',      cor: 'orange',  tip: 'Abertura adiada — nova data virá' },
  RET:   { txt: 'Retificada',  cor: 'purple',  tip: 'Edital retificado — conferir o que mudou' },
  RETRI: { txt: 'Retificada',  cor: 'purple',  tip: 'Edital retificado — conferir o que mudou' },
  ALTER: { txt: 'Alterada',    cor: 'purple',  tip: 'Alteração publicada — conferir o que mudou' },
  ANULA: { txt: 'Anulada',     cor: 'red',     tip: 'Licitação anulada pelo órgão' },
  REVO:  { txt: 'Revogada',    cor: 'red',     tip: 'Licitação revogada pelo órgão' },
  CANCE: { txt: 'Cancelada',   cor: 'red',     tip: 'Licitação cancelada pelo órgão' },
  EDITAL:{ txt: 'Edital',      cor: 'default', tip: 'Edital publicado' },
};

/**
 * Traduz a situação para gente. No ConLicitação vem código (PRO, SUS…);
 * no PNCP já vem texto ("Divulgada no PNCP") e passa direto. Devolve null
 * quando não há situação.
 */
export function situacaoLegivel(
  situacao: string | null | undefined,
  fonte?: string | null,
): SituacaoLegivel | null {
  const bruto = String(situacao || '').trim();
  if (!bruto) return null;
  if (fonte === 'pncp') {
    return { txt: bruto, cor: 'default', tip: 'Situação informada pelo PNCP' };
  }
  const info = SITUACOES_CONLIC[bruto.toUpperCase()];
  if (info) return { ...info, conhecida: true };
  // Código desconhecido (ou licitação manual com texto livre): mostra como veio.
  return { txt: bruto, cor: 'default', tip: 'Situação como veio da fonte' };
}

// Só aceita o que parece data ISO — o raw_json do PNCP tem campos vazios e
// o do ConLicitação tem texto tipo "a definir".
const RE_ISO = /^\d{4}-\d{2}-\d{2}/;
function isoOuNull(v: unknown): string | null {
  return typeof v === 'string' && RE_ISO.test(v) ? v : null;
}

/**
 * Prazo efetivo no navegador: usa `prazo_efetivo` quando o backend mandou;
 * senão repete a régua dele (só fallback — a régua oficial é a do SQL).
 */
export function prazoEfetivoCliente(det: any): string | null {
  if (!det) return null;
  return isoOuNull(det.prazo_efetivo)
    ?? isoOuNull(det.raw_json?.dataEncerramentoProposta)
    ?? isoOuNull(det.raw_json?.datahora_prazo)
    ?? isoOuNull(det.data_abertura)
    ?? null;
}

export type OrigemPrazo =
  | 'pncp:dataEncerramentoProposta'
  | 'conlicitacao:datahora_prazo'
  | 'fallback:data_abertura';

/** De onde saiu o prazo: o que o backend disse, ou derivado pela mesma régua. */
export function origemPrazoCliente(det: any): OrigemPrazo | null {
  if (!det) return null;
  if (det.origem_prazo) return det.origem_prazo as OrigemPrazo;
  if (isoOuNull(det.raw_json?.dataEncerramentoProposta)) return 'pncp:dataEncerramentoProposta';
  if (isoOuNull(det.raw_json?.datahora_prazo)) return 'conlicitacao:datahora_prazo';
  if (isoOuNull(det.data_abertura)) return 'fallback:data_abertura';
  return null;
}

/** Texto do Tooltip que explica a origem do prazo mostrado. */
export function descreverOrigemPrazo(origem: OrigemPrazo | string | null | undefined): string {
  switch (origem) {
    case 'pncp:dataEncerramentoProposta': return 'Prazo do PNCP (encerramento do recebimento de propostas)';
    case 'conlicitacao:datahora_prazo':   return 'Prazo do boletim do ConLicitação';
    case 'fallback:data_abertura':        return 'Só temos a data de abertura — a fonte não informou prazo de proposta';
    // A mesma licitação pelas duas fontes (07/10/2026): o mais atualizado entre elas.
    case 'unificada:pncp':                return 'Prazo mais atualizado entre as fontes — veio do PNCP';
    case 'unificada:conlicitacao':        return 'Prazo mais atualizado entre as fontes — veio do ConLicitação';
    default:                              return String(origem || '').startsWith('unificada:')
      ? 'Prazo mais atualizado entre as fontes'
      : 'Origem do prazo não informada';
  }
}
